import { useState, useMemo } from "react";
import { FullscreenLoader } from "@/components/FullscreenLoader";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useSearchParamState, useSearchParamNumber } from "@/hooks/useSearchParamState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Loader2,
  MoreVertical,
  Pencil,
  Trash2,
  Send,
  Copy,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  RotateCcw,
  Search,
  ArrowUpDown,
  Check,
  X,
  Minus,
  List,
  LayoutList,
  Users,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import type { Path } from "@/types/path";
import { deepCopyPathVersion } from "@/lib/deepCopyPath";
import { findSuggestionRankConflict, parseSuggestionRank } from "@/lib/pathSuggestionRank";
import { ItemMetadata } from "@/components/activities/ItemMetadata";
import { PageHelpButton } from "@/components/PageHelpButton";

interface PathGroup {
  rootId: string;
  title: string;
  latestPublished: Path | null;
  draft: Path | null;
  publishedVersions: Path[];
  // For sorting
  newestUpdatedAt: string;
  newestCreatedAt: string;
}

function groupPaths(paths: Path[]): PathGroup[] {
  const map = new Map<string, Path[]>();
  for (const p of paths) {
    const rootId = p.path_root_id || p.id;
    if (!map.has(rootId)) map.set(rootId, []);
    map.get(rootId)!.push(p);
  }

  const groups: PathGroup[] = [];
  for (const [rootId, items] of map) {
    items.sort((a, b) => b.version - a.version);
    const published = items.filter((p) => p.is_published);
    const draft = items.find((p) => !p.is_published) || null;
    const latestPublished = published.length > 0 ? published[0] : null;
    const display = draft || latestPublished!;
    groups.push({
      rootId,
      title: items[0].title,
      latestPublished,
      draft,
      publishedVersions: published,
      newestUpdatedAt: display.updated_at,
      newestCreatedAt: display.created_at,
    });
  }

  return groups;
}

type ViewMode = "detailed" | "compact";
type SortField = "updated_at" | "created_at" | "importance";
type SortDir = "desc" | "asc";

/** Ancien tri « points » dans l’URL → importance */
function normalizeSortFieldParam(raw: string): SortField {
  if (raw === "created_at") return "created_at";
  if (raw === "importance" || raw === "points") return "importance";
  return "updated_at";
}

/** Liste admin / tri : sans rang en métadonnées → dernier parmi les valeurs connues */
function suggestionRankForSort(metadata: unknown): number {
  return parseSuggestionRank(metadata) ?? Number.MAX_SAFE_INTEGER;
}

type StatusFilter = "all" | "draft" | "published";
type TypeFilter = "all" | "path" | "onboarding" | "meteo";

export default function Paths() {
  const { user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmAction, setConfirmAction] = useState<{ type: "publish" | "delete" | "delete-version" | "unpublish"; path: Path } | null>(null);
  const [initForAllDialog, setInitForAllDialog] = useState<Path | null>(null);
  const [initForAllLoading, setInitForAllLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Toolbar state (persisted in URL for back-navigation)
  const [search, setSearch] = useSearchParamState("q", "");
  const [statusFilter, setStatusFilter] = useSearchParamState("status", "all") as [StatusFilter, (v: string) => void];
  const [typeFilter, setTypeFilter] = useSearchParamState("type", "all") as [TypeFilter, (v: string) => void];
  const [sortFieldRaw, setSortFieldParam] = useSearchParamState("sort", "updated_at");
  const sortField = normalizeSortFieldParam(sortFieldRaw);
  const setSortField = (v: string) => setSortFieldParam(normalizeSortFieldParam(v));
  const [sortDir, setSortDir] = useSearchParamState("dir", "desc") as [SortDir, (v: string) => void];
  const [page, setPage] = useSearchParamNumber("page", 1);
  const [pageSize, setPageSizeRaw] = useSearchParamNumber("size", Number(localStorage.getItem("paths-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeRaw(v); localStorage.setItem("paths-page-size", String(v)); };
  const [viewMode, setViewModeState] = useState<ViewMode>(() => (localStorage.getItem("paths-view-mode") as ViewMode) || "detailed");
  const setViewMode = (v: ViewMode) => { setViewModeState(v); localStorage.setItem("paths-view-mode", v); };

  const { data: paths, isLoading } = useQuery({
    queryKey: ["paths"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("path")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Path[];
    },
  });

  const { data: onboardingSetting } = useQuery({
    queryKey: ["settings", "onboarding"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "onboarding")
        .single();
      if (error) return null;
      return data?.value as { default_path_id?: string } | null;
    },
  });

  const defaultOnboardingPathId = onboardingSetting?.default_path_id || null;

  const { data: meteoSetting } = useQuery({
    queryKey: ["settings", "meteo"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("value")
        .eq("key", "meteo")
        .single();
      if (error) return null;
      return data?.value as { first_path_id?: string | null; week_start_day?: number } | null;
    },
  });

  const meteoFirstPathId = meteoSetting?.first_path_id || null;

  const setDefaultOnboardingMutation = useMutation({
    mutationFn: async (pathId: string) => {
      const newValue = { ...(onboardingSetting || {}), default_path_id: pathId };
      const { error } = await supabase
        .from("settings")
        .update({ value: newValue as any, updated_by: user?.id })
        .eq("key", "onboarding");
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Parcours d'onboarding par défaut mis à jour");
      queryClient.invalidateQueries({ queryKey: ["settings", "onboarding"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const setMeteoFirstPathMutation = useMutation({
    mutationFn: async (pathId: string) => {
      const newValue = { ...(meteoSetting || {}), first_path_id: pathId };
      const { error } = await supabase
        .from("settings")
        .update({ value: newValue as any, updated_by: user?.id })
        .eq("key", "meteo");
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Première météo mise à jour");
      queryClient.invalidateQueries({ queryKey: ["settings", "meteo"] });
    },
    onError: () => toast.error("Erreur lors de la mise à jour"),
  });

  const allGroups = useMemo(() => (paths ? groupPaths(paths) : []), [paths]);

  // Filter, search, sort
  const filteredGroups = useMemo(() => {
    let result = allGroups;

    // Status filter
    if (statusFilter === "draft") {
      result = result.filter((g) => g.draft != null);
    } else if (statusFilter === "published") {
      result = result.filter((g) => g.latestPublished != null && !g.draft);
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter((g) => {
        const display = g.draft || g.latestPublished;
        return display?.path_type === typeFilter;
      });
    }

    // Search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((g) => g.title.toLowerCase().includes(q));
    }

    // Sort
    result = [...result].sort((a, b) => {
      if (sortField === "importance") {
        const aDisplay = a.draft || a.latestPublished;
        const bDisplay = b.draft || b.latestPublished;
        const aRank = suggestionRankForSort(aDisplay?.metadata);
        const bRank = suggestionRankForSort(bDisplay?.metadata);
        // Rang plus petit = plus important ; desc = les plus importants d'abord ; sans rang → dernier
        return sortDir === "desc" ? aRank - bRank : bRank - aRank;
      }
      const aVal = sortField === "updated_at" ? a.newestUpdatedAt : a.newestCreatedAt;
      const bVal = sortField === "updated_at" ? b.newestUpdatedAt : b.newestCreatedAt;
      return sortDir === "desc" ? bVal.localeCompare(aVal) : aVal.localeCompare(bVal);
    });

    return result;
  }, [allGroups, statusFilter, typeFilter, search, sortField, sortDir]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredGroups.length / pageSize));
  const safeCurrentPage = Math.min(page, totalPages);
  const paginatedGroups = filteredGroups.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

  // Reset page when filters change
  const handleSearchChange = (v: string) => { setSearch(v); setPage(1); };
  const handleStatusChange = (v: StatusFilter) => { setStatusFilter(v); setPage(1); };
  const handleTypeChange = (v: TypeFilter) => { setTypeFilter(v); setPage(1); };
  const handleSortToggle = () => {
    // Cycle: MAJ ↓ → MAJ ↑ → Création ↓ → Création ↑ → Importance ↓ → Importance ↑ → MAJ ↓
    if (sortField === "updated_at" && sortDir === "desc") {
      setSortDir("asc");
    } else if (sortField === "updated_at" && sortDir === "asc") {
      setSortField("created_at");
      setSortDir("desc");
    } else if (sortField === "created_at" && sortDir === "desc") {
      setSortDir("asc");
    } else if (sortField === "created_at" && sortDir === "asc") {
      setSortField("importance");
      setSortDir("desc");
    } else if (sortField === "importance" && sortDir === "desc") {
      setSortDir("asc");
    } else {
      setSortField("updated_at");
      setSortDir("desc");
    }
  };

  const toggleGroup = (rootId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(rootId)) next.delete(rootId); else next.add(rootId);
      return next;
    });
  };

  const publishMutation = useMutation({
    mutationFn: async (path: Path) => {
      const { error } = await supabase
        .from("path")
        .update({ is_published: true, updated_by: user?.id })
        .eq("id", path.id);
      if (error) throw error;
      const { data: pas } = await supabase
        .from("path_activity")
        .select("activity_id")
        .eq("path_id", path.id);
      if (pas && pas.length > 0) {
        const activityIds = pas.map((pa) => pa.activity_id);
        await supabase
          .from("activity")
          .update({ is_published: true, updated_by: user?.id })
          .in("id", activityIds);
      }
    },
    onSuccess: () => {
      toast.success("Parcours publié");
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la publication"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (path: Path) => {
      const { data: pas } = await supabase
        .from("path_activity")
        .select("activity_id")
        .eq("path_id", path.id);
      const activityIds = pas?.map((pa) => pa.activity_id) || [];

      if (activityIds.length > 0) {
        const { data: stepsData } = await supabase
          .from("activity_step")
          .select("id, card_id, card_group_id")
          .in("activity_id", activityIds);
        const stepIds = stepsData?.map((s) => s.id) || [];
        const cardIds = stepsData?.map((s) => s.card_id).filter(Boolean) as string[];
        const groupIds = stepsData?.map((s) => s.card_group_id).filter(Boolean) as string[];

        await supabase.from("activity").update({ first_step_id: null }).in("id", activityIds);

        if (stepIds.length > 0) {
          await supabase.from("activity_step_transition").delete().in("from_activity_step_id", stepIds);
        }
        if (groupIds.length > 0) {
          const { data: groupItems } = await supabase.from("card_group_item").select("id, card_id").in("card_group_id", groupIds);
          const groupCardIds = groupItems?.map((gi) => gi.card_id) || [];
          await supabase.from("card_group_item").delete().in("card_group_id", groupIds);
          if (groupCardIds.length > 0) await supabase.from("card").delete().in("id", groupCardIds);
        }
        if (stepIds.length > 0) await supabase.from("activity_step").delete().in("id", stepIds);
        if (cardIds.length > 0) await supabase.from("card").delete().in("id", cardIds);
        if (groupIds.length > 0) await supabase.from("card_group").delete().in("id", groupIds);
        await supabase.from("activity").delete().in("id", activityIds);
      }

      await supabase.from("path_activity").delete().eq("path_id", path.id);
      const { error } = await supabase.from("path").delete().eq("id", path.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Brouillon supprimé");
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la suppression"),
  });

  const deleteVersionMutation = useMutation({
    mutationFn: async (path: Path) => {
      const { data: pas } = await supabase.from("path_activity").select("activity_id").eq("path_id", path.id);
      const activityIds = pas?.map((pa) => pa.activity_id) || [];

      if (activityIds.length > 0) {
        const { data: stepsData } = await supabase.from("activity_step").select("id, card_id, card_group_id").in("activity_id", activityIds);
        const stepIds = stepsData?.map((s) => s.id) || [];
        const cardIds = stepsData?.map((s) => s.card_id).filter(Boolean) as string[];
        const groupIds = stepsData?.map((s) => s.card_group_id).filter(Boolean) as string[];

        await supabase.from("activity").update({ first_step_id: null }).in("id", activityIds);
        if (stepIds.length > 0) await supabase.from("activity_step_transition").delete().in("from_activity_step_id", stepIds);
        if (groupIds.length > 0) {
          const { data: groupItems } = await supabase.from("card_group_item").select("id, card_id").in("card_group_id", groupIds);
          const groupCardIds = groupItems?.map((gi) => gi.card_id) || [];
          await supabase.from("card_group_item").delete().in("card_group_id", groupIds);
          if (groupCardIds.length > 0) await supabase.from("card").delete().in("id", groupCardIds);
        }
        if (stepIds.length > 0) await supabase.from("activity_step").delete().in("id", stepIds);
        if (cardIds.length > 0) await supabase.from("card").delete().in("id", cardIds);
        if (groupIds.length > 0) await supabase.from("card_group").delete().in("id", groupIds);
        await supabase.from("activity").delete().in("id", activityIds);
      }

      await supabase.from("path_activity").delete().eq("path_id", path.id);
      const { error } = await supabase.from("path").delete().eq("id", path.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Version supprimée");
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors de la suppression de la version"),
  });

  const unpublishMutation = useMutation({
    mutationFn: async (path: Path) => {
      const { error } = await supabase.from("path").update({ is_published: false, updated_by: user?.id }).eq("id", path.id);
      if (error) throw error;
      const { data: pas } = await supabase.from("path_activity").select("activity_id").eq("path_id", path.id);
      if (pas && pas.length > 0) {
        const activityIds = pas.map((pa) => pa.activity_id);
        await supabase.from("activity").update({ is_published: false, updated_by: user?.id }).in("id", activityIds);
      }
    },
    onSuccess: () => {
      toast.success("Version repassée en brouillon");
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      setConfirmAction(null);
    },
    onError: () => toast.error("Erreur lors du passage en brouillon"),
  });

  const [versionLoading, setVersionLoading] = useState(false);

  const createNewVersion = async (path: Path) => {
    if (!user) return;
    setVersionLoading(true);
    try {
      const newPath = await deepCopyPathVersion(path, user.id);
      toast.success(`Nouvelle version (v${newPath.version}) créée`);
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      navigate(`/paths/${newPath.id}`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Erreur inconnue";
      console.error("Erreur deep copy:", message, err);
      toast.error(`Erreur lors de la création de la nouvelle version: ${message}`);
    } finally {
      setVersionLoading(false);
    }
  };

  const handleInitForAll = async () => {
    if (!initForAllDialog) return;
    setInitForAllLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users?action=init_path_for_all`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ path_id: initForAllDialog.id }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Erreur");
      toast.success(`Parcours initialisé pour ${result.initialized_count} utilisateur(s)`);
      setInitForAllDialog(null);
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de l'initialisation");
    } finally {
      setInitForAllLoading(false);
    }
  };

  const sortLabel = sortField === "updated_at"
    ? `MAJ ${sortDir === "desc" ? "↓" : "↑"}`
    : sortField === "importance"
    ? `Importance ${sortDir === "desc" ? "↓" : "↑"}`
    : `Création ${sortDir === "desc" ? "↓" : "↑"}`;

  return (
    <>
    {versionLoading && <FullscreenLoader message="Création de la nouvelle version en cours…" />}
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-tour="paths-title">Parcours</h1>
          <p className="mt-1 text-muted-foreground">Gérez les parcours et leurs activités</p>
        </div>
        <div className="flex items-center gap-2">
          <PageHelpButton tourKey="paths" />
          <Button onClick={() => navigate("/paths/new")} data-tour="paths-create">
            <Plus className="mr-2 h-4 w-4" />
            Nouveau parcours
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3" data-tour="paths-toolbar">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un parcours…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => handleStatusChange(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            <SelectItem value="draft">Brouillons</SelectItem>
            <SelectItem value="published">Publiés</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={(v) => handleTypeChange(v as TypeFilter)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            <SelectItem value="path">Parcours</SelectItem>
            <SelectItem value="onboarding">Onboarding</SelectItem>
            <SelectItem value="meteo">Météo mentale</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleSortToggle} className="gap-1.5">
          <ArrowUpDown className="h-3.5 w-3.5" />
          {sortLabel}
        </Button>
        <TooltipProvider delayDuration={300}>
          <div className="flex items-center border border-border rounded-md">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={viewMode === "compact" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-r-none"
                  onClick={() => setViewMode("compact")}
                >
                  <List className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Vue compacte</p></TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={viewMode === "detailed" ? "secondary" : "ghost"}
                  size="icon"
                  className="h-8 w-8 rounded-l-none"
                  onClick={() => setViewMode("detailed")}
                >
                  <LayoutList className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent><p>Vue détaillée</p></TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : paginatedGroups.length > 0 ? (
        <>
          <div className="grid gap-3" data-tour="paths-list">
            {paginatedGroups.map((group) => (
              <PathGroupCard
                key={group.rootId}
                group={group}
                isSuperAdmin={isSuperAdmin}
                isExpanded={expandedGroups.has(group.rootId)}
                viewMode={viewMode}
                onToggle={() => toggleGroup(group.rootId)}
                onNavigate={(id) => navigate(`/paths/${id}`)}
                onPublish={(p) => setConfirmAction({ type: "publish", path: p })}
                onDelete={(p) => setConfirmAction({ type: "delete", path: p })}
                onDeleteVersion={(p) => setConfirmAction({ type: "delete-version", path: p })}
                onUnpublish={(p) => setConfirmAction({ type: "unpublish", path: p })}
                onNewVersion={createNewVersion}
                defaultOnboardingPathId={defaultOnboardingPathId}
                onSetDefaultOnboarding={(id) => setDefaultOnboardingMutation.mutate(id)}
                meteoFirstPathId={meteoFirstPathId || null}
                onSetMeteoFirstPath={(id) => setMeteoFirstPathMutation.mutate(id)}
                onInitForAll={(p) => setInitForAllDialog(p)}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{filteredGroups.length} résultat{filteredGroups.length > 1 ? "s" : ""}</span>
              <span>·</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span>par page</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safeCurrentPage <= 1} onClick={() => setPage(safeCurrentPage - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="px-2 text-sm text-muted-foreground">
                {safeCurrentPage} / {totalPages}
              </span>
              <Button variant="outline" size="icon" className="h-8 w-8" disabled={safeCurrentPage >= totalPages} onClick={() => setPage(safeCurrentPage + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </>
      ) : (
        <Card className="border-border/50 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <p className="text-muted-foreground">
              {search || statusFilter !== "all" ? "Aucun parcours ne correspond aux filtres" : "Aucun parcours pour le moment"}
            </p>
            {!search && statusFilter === "all" && (
              <Button variant="outline" className="mt-4" onClick={() => navigate("/paths/new")}>
                <Plus className="mr-2 h-4 w-4" />
                Créer votre premier parcours
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === "publish" && "Publier ce parcours ?"}
              {confirmAction?.type === "delete" && "Supprimer ce brouillon ?"}
              {confirmAction?.type === "delete-version" && `Supprimer la version v${confirmAction.path.version} ?`}
              {confirmAction?.type === "unpublish" && `Repasser la v${confirmAction.path.version} en brouillon ?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction?.type === "publish" && "Un parcours publié devient immuable. Vous devrez créer une nouvelle version pour le modifier."}
              {confirmAction?.type === "delete" && "Cette action est irréversible. Le brouillon et ses activités associées seront définitivement supprimés."}
              {confirmAction?.type === "delete-version" && "Cette action est irréversible. La version publiée et toutes ses dépendances (activités, cartes, etc.) seront définitivement supprimées."}
              {confirmAction?.type === "unpublish" && "Le parcours et toutes ses activités associées repasseront en brouillon et seront modifiables."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.type === "delete" || confirmAction?.type === "delete-version" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              onClick={() => {
                if (!confirmAction) return;
                if (confirmAction.type === "publish") publishMutation.mutate(confirmAction.path);
                else if (confirmAction.type === "delete") deleteMutation.mutate(confirmAction.path);
                else if (confirmAction.type === "delete-version") deleteVersionMutation.mutate(confirmAction.path);
                else if (confirmAction.type === "unpublish") unpublishMutation.mutate(confirmAction.path);
              }}
            >
              {confirmAction?.type === "publish" && "Publier"}
              {confirmAction?.type === "delete" && "Supprimer"}
              {confirmAction?.type === "delete-version" && "Supprimer"}
              {confirmAction?.type === "unpublish" && "Repasser en brouillon"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!initForAllDialog} onOpenChange={(open) => !open && setInitForAllDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Initialiser ce parcours pour tous les utilisateurs ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le parcours « {initForAllDialog?.title} » (v{initForAllDialog?.version}) sera ajouté en fin de liste pour tous les utilisateurs ayant terminé leur onboarding et ne l'ayant pas encore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={initForAllLoading}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleInitForAll} disabled={initForAllLoading}>
              {initForAllLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Initialiser
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </>
  );
}

/* ─── Rang éditorial (départage à score égal, ≠ ordre utilisateur final) ─ */

function InlineImportanceRankEditor({ path }: { path: Path }) {
  const [editing, setEditing] = useState(false);
  const [draftRank, setDraftRank] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const storedRank = parseSuggestionRank(path.metadata);

  const saveMutation = useMutation({
    mutationFn: async (newRank: number | null) => {
      if (newRank !== null) {
        const { data: rankPaths, error: rankError } = await supabase
          .from("path")
          .select("id, title, path_root_id, path_type, version, is_published, metadata")
          .eq("path_type", "path");
        if (rankError) throw rankError;

        const conflict = findSuggestionRankConflict(rankPaths || [], newRank, path.id);
        if (conflict) {
          throw new Error(`Le rang ${newRank} est déjà utilisé par « ${conflict.title} ».`);
        }
      }

      const meta = { ...(path.metadata as Record<string, unknown> | null | undefined) } as Record<string, unknown>;
      if (newRank === null) delete meta.suggestion_importance_rank;
      else meta.suggestion_importance_rank = newRank;
      const { error } = await supabase
        .from("path")
        .update({
          metadata: meta as never,
          updated_by: user?.id,
        })
        .eq("id", path.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["paths"] });
      setEditing(false);
    },
    onError: (error: Error) => toast.error(error.message || "Erreur lors de la sauvegarde"),
  });

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraftRank(storedRank);
    setEditing(true);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    saveMutation.mutate(draftRank);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditing(false);
  };

  const rankLabel = storedRank === null ? "Non défini" : `r.${storedRank}`;
  const rankTooltip =
    storedRank === null
      ? "Aucun rang éditorial enregistré en métadonnées : à score de personnalisation égal, ce parcours passe après ceux qui ont un rang. L’ordre affiché par utilisateur dans l’app reste personnalisé."
      : "Rang éditorial pour départager les parcours à score de personnalisation égal. L’ordre réellement proposé à chaque utilisateur après onboarding est personnalisé et ne correspond pas à cet indicateur.";

  if (!editing) {
    return (
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge
              variant="outline"
              className="text-xs cursor-pointer hover:bg-accent/50 transition-colors max-w-[9rem] truncate"
              onClick={startEdit}
            >
              {rankLabel}
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            <p>{rankTooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="flex items-center gap-1 rounded-md border border-border bg-background px-1 py-0.5"
        onClick={(e) => e.stopPropagation()}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            saveMutation.mutate(draftRank);
          }
        }}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              disabled={draftRank === null}
              onClick={(e) => {
                e.stopPropagation();
                setDraftRank((d) => (d === null ? null : d - 1));
              }}
            >
              <Minus className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p>Plus prioritaire (rang plus petit)</p></TooltipContent>
        </Tooltip>
        <Input
          type="number"
          className="w-14 h-6 text-center text-xs px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          value={draftRank === null ? "" : String(draftRank)}
          autoFocus
          onChange={(e) => {
            const t = e.target.value.trim();
            if (t === "") {
              setDraftRank(null);
              return;
            }
            const n = parseInt(t, 10);
            if (Number.isFinite(n)) setDraftRank(n);
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setDraftRank((d) => (d === null ? 1 : d + 1));
              }}
            >
              <Plus className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p>Moins prioritaire (rang plus grand)</p></TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-primary"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              <Check className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p>Enregistrer</p></TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground"
              onClick={handleCancel}
            >
              <X className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom"><p>Annuler</p></TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

/* ─── Path Group Card ─────────────────────────────────── */

function PathGroupCard({
  group,
  isSuperAdmin,
  isExpanded,
  viewMode,
  onToggle,
  onNavigate,
  onPublish,
  onDelete,
  onDeleteVersion,
  onUnpublish,
  onNewVersion,
  defaultOnboardingPathId,
  onSetDefaultOnboarding,
  meteoFirstPathId,
  onSetMeteoFirstPath,
  onInitForAll,
}: {
  group: PathGroup;
  isSuperAdmin: boolean;
  isExpanded: boolean;
  viewMode: ViewMode;
  onToggle: () => void;
  onNavigate: (id: string) => void;
  onPublish: (p: Path) => void;
  onDelete: (p: Path) => void;
  onDeleteVersion: (p: Path) => void;
  onUnpublish: (p: Path) => void;
  onNewVersion: (p: Path) => void;
  defaultOnboardingPathId: string | null;
  onSetDefaultOnboarding: (pathId: string) => void;
  meteoFirstPathId: string | null;
  onSetMeteoFirstPath: (pathId: string) => void;
  onInitForAll: (p: Path) => void;
}) {
  const displayPath = group.draft || group.latestPublished!;
  const hasVersions = group.publishedVersions.length > 0;
  const isCompact = viewMode === "compact";

  const handleCardClick = () => {
    if (group.draft) {
      onNavigate(group.draft.id);
    } else if (group.latestPublished) {
      onNavigate(group.latestPublished.id);
    }
  };

  const pathTypeLabel = displayPath.path_type === "onboarding" ? "Onboarding" : displayPath.path_type === "meteo" ? "Météo mentale" : "Parcours";
  const pathTypeBadgeClass = displayPath.path_type === "onboarding"
    ? "bg-emerald-500/15 text-emerald-700 border-emerald-500/30"
    : displayPath.path_type === "meteo"
    ? "bg-sky-500/15 text-sky-700 border-sky-500/30"
    : "bg-violet-500/15 text-violet-700 border-violet-500/30";

  const dropdownMenu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {group.draft && (
          <DropdownMenuItem onClick={() => onNavigate(group.draft!.id)}>
            <Pencil className="mr-2 h-4 w-4" />Modifier le brouillon
          </DropdownMenuItem>
        )}
        {group.draft && (
          <DropdownMenuItem onClick={() => onPublish(group.draft!)}>
            <Send className="mr-2 h-4 w-4" />Publier le brouillon
          </DropdownMenuItem>
        )}
        {group.latestPublished && !group.draft && (
          <DropdownMenuItem onClick={() => onNewVersion(group.latestPublished!)}>
            <Copy className="mr-2 h-4 w-4" />Nouvelle version
          </DropdownMenuItem>
        )}
        {group.latestPublished && (
          <DropdownMenuItem onClick={() => onNavigate(group.latestPublished!.id)}>
            <Eye className="mr-2 h-4 w-4" />Voir la dernière version publiée
          </DropdownMenuItem>
        )}
        {isSuperAdmin && group.latestPublished && !group.draft && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onUnpublish(group.latestPublished!)}>
              <RotateCcw className="mr-2 h-4 w-4" />Repasser en brouillon
            </DropdownMenuItem>
          </>
        )}
        {displayPath.path_type === "path" && group.latestPublished && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onInitForAll(group.latestPublished!)}>
              <Users className="mr-2 h-4 w-4" />Initialiser pour tous les utilisateurs
            </DropdownMenuItem>
          </>
        )}
        {hasVersions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onToggle}>
              <ChevronDown className="mr-2 h-4 w-4" />
              {isExpanded ? "Masquer les versions" : "Voir toutes les versions"}
            </DropdownMenuItem>
          </>
        )}
        {group.draft && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => onDelete(group.draft!)}>
              <Trash2 className="mr-2 h-4 w-4" />Supprimer le brouillon
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const versionHistory = isExpanded && hasVersions && (
    <CardContent className="pt-0 pb-4">
      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-medium text-muted-foreground mb-2">Historique des versions</p>
        {group.publishedVersions.map((v, index) => (
          <div
            key={v.id}
            className="flex items-center justify-between px-3 py-2 rounded-md border border-border/50 hover:bg-accent/50 cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); onNavigate(v.id); }}
          >
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">v{v.version}</Badge>
              <span className="text-sm">{v.title}</span>
            </div>
            <ItemMetadata
              createdAt={v.created_at}
              createdBy={v.created_by}
              updatedAt={v.updated_at}
              updatedBy={v.updated_by}
              compact
            />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{new Date(v.created_at).toLocaleDateString("fr-FR")}</span>
              {isSuperAdmin ? (
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => e.stopPropagation()}>
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem onClick={() => onNavigate(v.id)}>
                      <Eye className="mr-2 h-4 w-4" />Voir cette version
                    </DropdownMenuItem>
                    {index === 0 && !group.draft && (
                      <DropdownMenuItem onClick={() => onUnpublish(v)}>
                        <RotateCcw className="mr-2 h-4 w-4" />Repasser en brouillon
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => onDeleteVersion(v)}>
                      <Trash2 className="mr-2 h-4 w-4" />Supprimer cette version
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Eye className="h-3.5 w-3.5" />
              )}
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  );

  if (isCompact) {
    return (
      <Card className="border-border/50 cursor-pointer hover:bg-accent/30 transition-colors" onClick={handleCardClick}>
        <CardHeader className="py-2 px-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium truncate min-w-0">{group.title}</span>
            {displayPath.path_type === "path" && (
              <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                <InlineImportanceRankEditor path={displayPath} />
              </div>
            )}
            <div className="flex-1" />
            <Badge variant="outline" className={`text-[10px] py-0 shrink-0 ${pathTypeBadgeClass}`}>
              {pathTypeLabel}
            </Badge>
            {displayPath.path_type === "onboarding" && group.latestPublished && group.latestPublished.id === defaultOnboardingPathId && (
              <Badge className="text-[10px] py-0 bg-emerald-500/15 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/15 shrink-0">
                Par défaut
              </Badge>
            )}
            {displayPath.path_type === "meteo" && group.latestPublished && group.latestPublished.id === meteoFirstPathId && (
              <Badge className="text-[10px] py-0 bg-sky-500/15 text-sky-700 border-sky-500/30 hover:bg-sky-500/15 shrink-0">
                1ère météo
              </Badge>
            )}
            <ItemMetadata
              createdAt={displayPath.created_at}
              createdBy={displayPath.created_by}
              updatedAt={displayPath.updated_at}
              updatedBy={displayPath.updated_by}
              compact
            />
            {group.latestPublished && (
              <Badge variant="default" className="text-[10px] py-0 shrink-0">
                v{group.latestPublished.version}
              </Badge>
            )}
            {group.draft && (
              <Badge variant="secondary" className="text-[10px] py-0 shrink-0">
                Brouillon v{group.draft.version}
              </Badge>
            )}
            {dropdownMenu}
          </div>
        </CardHeader>
        {versionHistory}
      </Card>
    );
  }

  return (
    <Card className="border-border/50 cursor-pointer hover:bg-accent/30 transition-colors" onClick={handleCardClick}>
      <CardHeader className="py-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{group.title}</CardTitle>
              {displayPath.path_type === "path" && (
                <InlineImportanceRankEditor path={displayPath} />
              )}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <Badge variant="outline" className={`text-xs ${pathTypeBadgeClass}`}>
                {pathTypeLabel}
              </Badge>
              {displayPath.path_type === "onboarding" && group.latestPublished && (
                group.latestPublished.id === defaultOnboardingPathId ? (
                  <Badge className="text-xs bg-emerald-500/15 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/15">
                    Onboarding par défaut
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); onSetDefaultOnboarding(group.latestPublished!.id); }}
                  >
                    Définir par défaut
                  </Button>
                )
              )}
              {displayPath.path_type === "meteo" && group.latestPublished && (
                group.latestPublished.id === meteoFirstPathId ? (
                  <Badge className="text-xs bg-sky-500/15 text-sky-700 border-sky-500/30 hover:bg-sky-500/15">
                    Première météo
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-xs text-muted-foreground hover:text-foreground"
                    onClick={(e) => { e.stopPropagation(); onSetMeteoFirstPath(group.latestPublished!.id); }}
                  >
                    Définir comme première météo
                  </Button>
                )
              )}
            </div>
            <ItemMetadata
              createdAt={displayPath.created_at}
              createdBy={displayPath.created_by}
              updatedAt={displayPath.updated_at}
              updatedBy={displayPath.updated_by}
            />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {group.latestPublished && (
              <Badge
                variant="default"
                className="cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onNavigate(group.latestPublished!.id); }}
              >
                Publiée v{group.latestPublished.version}
              </Badge>
            )}
            {group.draft && (
              <Badge
                variant="secondary"
                className="cursor-pointer hover:opacity-80 transition-opacity"
                onClick={(e) => { e.stopPropagation(); onNavigate(group.draft!.id); }}
              >
                Brouillon v{group.draft.version}
              </Badge>
            )}
            {dropdownMenu}
          </div>
        </div>
      </CardHeader>
      {versionHistory}
    </Card>
  );
}
